import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Scale, Printer, Search } from 'lucide-react'

import { api } from '@/api/client'
import type { LedgerResponse, LedgerRow } from '@/types/models'
import { cn, formatNum } from '@/lib/utils'
import { ReceivableDetailModal } from './ReceivableDetailModal'
import { PayableDetailModal } from './PayableDetailModal'

type Mode = 'receivable' | 'payable'

const _norm = (s: string) => s.replace(/\s+/g, '').toLowerCase()

export function LedgerTab() {
  const [mode, setMode] = useState<Mode>('receivable')
  const [dateFrom, setDateFrom] = useState('')   // 기본 빈 날짜(직접 지정)
  const [dateTo, setDateTo] = useState('')
  const [q, setQ] = useState('')
  // tx: 채권 쪽에서 [입고·지급·수금 내역]으로 거래내역 창을 연 경우
  const [detail, setDetail] = useState<{ id: number; name: string; tx?: boolean } | null>(null)

  const { data } = useQuery({
    queryKey: ['ledger', mode, dateFrom, dateTo],
    queryFn: () => api<LedgerResponse>(`/api/ledger/${mode}?from=${dateFrom}&to=${dateTo}`),
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })

  // 업체명·코드 검색(고객 요청 2026-10-15) — 화면에서 바로 거르고 합계도 걸러진 줄 기준.
  const view = useMemo(() => {
    if (!data) return null
    const nq = _norm(q)
    if (!nq) return data
    const rows = data.rows.filter((r) => _norm(r.partner_name).includes(nq) || r.partner_code.includes(nq))
    const keys = ['opening', 'sales', 'received', 'purchases', 'paid', 'balance'] as const
    const totals: Record<string, string> = {}
    for (const k of keys) {
      if (data.totals[k] !== undefined)
        totals[k] = String(rows.reduce((s, r: LedgerRow) => s + Number(r[k] ?? 0), 0))
    }
    return { ...data, rows, totals }
  }, [data, q])

  return (
    <div id="ledger-print" className="max-w-7xl mx-auto p-6">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #ledger-print, #ledger-print * { visibility: visible !important; }
        #ledger-print { position: absolute; left: 0; top: 0; width: 100%; }
        #ledger-print .no-print { display: none !important; }
        @page { size: A4 portrait; margin: 10mm; }
      }`}</style>
      <header className="mb-5 flex items-start gap-3">
        <Scale className="w-6 h-6 text-emerald-600 mt-0.5 no-print"/>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            채권채무집계 <span className="text-base font-semibold text-slate-500">— {mode === 'receivable' ? '채권(매출처)' : '채무(매입처)'}{dateFrom && dateTo ? ` · ${dateFrom} ~ ${dateTo}` : ''}</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            거래처별 잔액 = 전월이월 + 당기발생 − 당기수금/지급
            <span className={mode === 'receivable' ? 'text-emerald-600' : 'text-purple-600'}>
              {mode === 'receivable'
                ? ' · 거래처명을 클릭하면 상세현황(판매·수금·잔액), 그 창에서 입고·지급·수금 내역도'
                : ' · 거래처명을 클릭하면 거래내역(입고·지급·수금·미지급잔액)'}
            </span>
          </p>
        </div>
        <button onClick={() => window.print()} disabled={!data}
                className="no-print flex items-center gap-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          <Printer className="w-4 h-4"/> 인쇄
        </button>
      </header>

      <div className="no-print bg-white rounded-xl border border-slate-200 p-3 mb-3 flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
          <button onClick={() => setMode('receivable')}
                  className={cn('px-3 py-2 text-sm font-semibold transition',
                    mode === 'receivable' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
            채권 (매출처)
          </button>
          <button onClick={() => setMode('payable')}
                  className={cn('px-3 py-2 text-sm font-semibold transition',
                    mode === 'payable' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
            채무 (매입처)
          </button>
        </div>

        <label className="text-xs font-semibold text-slate-500 ml-3">기간</label>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
               className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm tabular-nums"/>
        <span className="text-slate-400">~</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
               className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm tabular-nums"/>

        <div className="relative ml-3">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="업체명·코드 검색"
                 className="pl-8 pr-2 py-1.5 border border-slate-300 rounded-lg text-sm w-56"/>
        </div>

        <span className="ml-auto text-xs text-slate-400 tabular-nums">
          {view ? (q.trim() && data ? `${view.rows.length} / ${data.rows.length}건` : `${view.rows.length}건`) : ''}
        </span>
      </div>

      {view && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium w-24">코드</th>
                <th className="px-3 py-2.5 text-left font-medium">거래처</th>
                <th className="px-3 py-2.5 text-right font-medium w-32">전월이월</th>
                <th className="px-3 py-2.5 text-right font-medium w-32">
                  {mode === 'receivable' ? '당기발생(매출)' : '당기발생(매입)'}
                </th>
                <th className="px-3 py-2.5 text-right font-medium w-32">
                  {mode === 'receivable' ? '당기수금' : '당기지급'}
                </th>
                <th className="px-3 py-2.5 text-right font-medium w-32">
                  {mode === 'receivable' ? '잔액(미수액)' : '잔액(미지급)'}
                </th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((r) => {
                const balance = Number(r.balance)
                return (
                  <tr key={r.partner_id}
                      onClick={() => setDetail({ id: r.partner_id, name: r.partner_name })}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs text-slate-500 tabular-nums">{r.partner_code}</td>
                    <td className="px-3 py-2 font-medium">
                      <span className={cn('hover:underline',
                        mode === 'receivable' ? 'text-emerald-700' : 'text-purple-700')}>{r.partner_name}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatNum(r.opening)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatNum(mode === 'receivable' ? r.sales : r.purchases)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {formatNum(mode === 'receivable' ? r.received : r.paid)}
                    </td>
                    <td className={cn('px-3 py-2 text-right tabular-nums font-bold',
                      mode === 'receivable'
                        ? balance > 0 ? 'text-rose-700' : balance < 0 ? 'text-emerald-700' : 'text-slate-400'
                        : balance > 0 ? 'text-purple-700' : balance < 0 ? 'text-emerald-700' : 'text-slate-400')}>
                      {formatNum(r.balance)}
                    </td>
                  </tr>
                )
              })}
              {!view.rows.length && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-10 text-sm">
                  {q.trim() ? '검색한 업체가 없습니다.' : '이 기간에 거래내역이 없습니다.'}
                </td></tr>
              )}
            </tbody>
            <tfoot className="bg-slate-50 font-bold">
              <tr>
                <td colSpan={2} className="px-3 py-2 text-right text-xs text-slate-600">합 계</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatNum(view.totals.opening)}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums',
                  mode === 'receivable' ? 'text-emerald-700' : 'text-slate-700')}>
                  {formatNum(mode === 'receivable' ? view.totals.sales : view.totals.purchases)}
                </td>
                <td className={cn('px-3 py-2 text-right tabular-nums',
                  mode === 'receivable' ? 'text-blue-700' : 'text-purple-700')}>
                  {formatNum(mode === 'receivable' ? view.totals.received : view.totals.paid)}
                </td>
                <td className={cn('px-3 py-2 text-right tabular-nums',
                  mode === 'receivable' ? 'text-rose-700' : 'text-purple-700')}>
                  {formatNum(view.totals.balance)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {detail && (mode === 'receivable' && !detail.tx
        ? <ReceivableDetailModal
            partnerId={detail.id} partnerName={detail.name}
            from={dateFrom} to={dateTo} onClose={() => setDetail(null)}
            onShowTx={() => setDetail({ ...detail, tx: true })} />
        : <PayableDetailModal
            partnerId={detail.id} partnerName={detail.name}
            from={dateFrom} to={dateTo} onClose={() => setDetail(null)} />)}
    </div>
  )
}
